"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CssSelectorWhitelistError = void 0;
exports.validateCssAgainstSelectorWhitelist = validateCssAgainstSelectorWhitelist;
exports.validateAppBaseThemeTokenName = validateAppBaseThemeTokenName;
exports.validateAppBaseThemeTokenValue = validateAppBaseThemeTokenValue;
exports.validateAppBaseThemeTokens = validateAppBaseThemeTokens;
exports.validateAppBaseMediaQuery = validateAppBaseMediaQuery;
const postcss_1 = __importDefault(require("postcss"));
const postcss_selector_parser_1 = __importDefault(require("postcss-selector-parser"));
const app_base_css_contract_1 = require("./app-base-css-contract");
class CssSelectorWhitelistError extends Error {
    constructor(selector, hint) {
        super(`Selector "${selector}" is outside the App CSS sandbox. ${hint}`);
        this.selector = selector;
        this.hint = hint;
        this.name = 'CssSelectorWhitelistError';
    }
}
exports.CssSelectorWhitelistError = CssSelectorWhitelistError;
const ALLOWED_DATA_ATTRS = new Set([
    ...Object.values(app_base_css_contract_1.APP_BASE_ROOT_ATTRS),
    ...Object.values(app_base_css_contract_1.APP_BASE_FLAG_ATTRS),
    app_base_css_contract_1.APP_BASE_FIELD_ATTR,
    app_base_css_contract_1.APP_BASE_FIELD_TYPE_ATTR,
    app_base_css_contract_1.APP_BASE_ROLE_ATTR,
    app_base_css_contract_1.APP_BASE_STATE_ATTR,
    'data-sl-component',
]);
const THEME_TOKEN_NAME_PATTERN = /^--sl-app-[a-zA-Z0-9-_]+$/;
const MEDIA_QUERY_SAFE_PATTERN = /^[a-zA-Z0-9\s():.,\-_/]+$/;
function isRootSelector(selector) {
    return (0, postcss_selector_parser_1.default)()
        .astSync(selector)
        .nodes.every(group => group.nodes.every(node => node.type === 'pseudo' && node.value === ':root'));
}
function assertRootRuleAllowed(selector, rule, options) {
    if (!isRootSelector(selector))
        return;
    if (!options.allowRootTokenDefinitions) {
        throw new CssSelectorWhitelistError(selector, ':root is only allowed for theme token definitions.');
    }
    const declarations = rule.nodes?.filter((node) => node.type === 'decl');
    if (!declarations?.length ||
        declarations.some(decl => !decl.prop.startsWith('--sl-app-'))) {
        throw new CssSelectorWhitelistError(selector, ':root may only define --sl-app-* custom properties.');
    }
}
function validateSelectorAst(selector) {
    let hasAllowedAnchor = false;
    (0, postcss_selector_parser_1.default)(selectors => {
        selectors.walkTags(tag => {
            const value = tag.value.toLowerCase();
            if (value === 'body' || value === 'html') {
                throw new CssSelectorWhitelistError(selector, 'Target .sl-app or data-sl-* anchors, not document-level tags.');
            }
        });
        selectors.walkPseudos(pseudo => {
            if (pseudo.value === ':root') {
                throw new CssSelectorWhitelistError(selector, ':root is only allowed for theme token definitions.');
            }
        });
        selectors.walkClasses(className => {
            if (className.value.startsWith('affine-') ||
                className.value.startsWith('_')) {
                throw new CssSelectorWhitelistError(selector, 'Workspace/internal classes are not part of the App CSS contract.');
            }
            if (className.value === app_base_css_contract_1.APP_BASE_SCOPE_CLASS ||
                className.value.startsWith(app_base_css_contract_1.APP_BASE_MOTION_CLASS_PREFIX)) {
                hasAllowedAnchor = true;
            }
        });
        selectors.walkAttributes(attr => {
            if (attr.attribute && ALLOWED_DATA_ATTRS.has(attr.attribute)) {
                hasAllowedAnchor = true;
            }
            if (attr.attribute?.startsWith('data-sl-') &&
                !ALLOWED_DATA_ATTRS.has(attr.attribute)) {
                throw new CssSelectorWhitelistError(selector, `Unknown data-sl-* attribute "${attr.attribute}".`);
            }
        });
    }).processSync(selector);
    if (!hasAllowedAnchor) {
        throw new CssSelectorWhitelistError(selector, 'Selectors must be scoped to .sl-app, App motion classes, or stable data-sl-* attributes.');
    }
}
function validateCssAgainstSelectorWhitelist(css, options = {}) {
    if (!css.trim())
        return;
    const root = postcss_1.default.parse(css);
    root.walkRules(rule => {
        for (const selector of rule.selectors) {
            assertRootRuleAllowed(selector, rule, options);
            if (isRootSelector(selector))
                continue;
            validateSelectorAst(selector);
        }
    });
}
function validateAppBaseThemeTokenName(name) {
    if (!THEME_TOKEN_NAME_PATTERN.test(name)) {
        throw new CssSelectorWhitelistError(':root', 'Theme token names must be --sl-app-* custom properties.');
    }
}
function validateAppBaseThemeTokenValue(name, value) {
    const trimmed = value.trim();
    if (!trimmed)
        return;
    validateAppBaseThemeTokenName(name);
    const root = postcss_1.default.parse(`:root { ${name}: ${trimmed}; }`);
    const rule = root.first;
    if (!rule || rule.type !== 'rule') {
        throw new CssSelectorWhitelistError(':root', `Theme token "${name}" has an invalid CSS value.`);
    }
    const declarations = rule.nodes?.filter((node) => node.type === 'decl');
    if (declarations?.length !== 1 ||
        declarations[0]?.prop !== name ||
        declarations[0]?.value.trim() !== trimmed ||
        (rule.nodes?.length ?? 0) !== 1) {
        throw new CssSelectorWhitelistError(':root', `Theme token "${name}" has an invalid CSS value.`);
    }
}
function validateAppBaseThemeTokens(tokens) {
    for (const [name, value] of Object.entries(tokens)) {
        if (!name || !value)
            continue;
        validateAppBaseThemeTokenValue(name, value);
    }
}
function validateAppBaseMediaQuery(media) {
    const trimmed = media.trim();
    if (!trimmed)
        return;
    if (!MEDIA_QUERY_SAFE_PATTERN.test(trimmed)) {
        throw new CssSelectorWhitelistError(`@media ${trimmed}`, 'Media queries may only contain media-condition syntax, not declarations or nested rules.');
    }
    const root = postcss_1.default.parse(`@media ${trimmed} { .sl-app { display:block } }`);
    const atRule = root.first;
    if (!atRule ||
        atRule.type !== 'atrule' ||
        atRule.name !== 'media' ||
        atRule.params.trim() !== trimmed ||
        (root.nodes?.length ?? 0) !== 1) {
        throw new CssSelectorWhitelistError(`@media ${trimmed}`, 'Media query syntax is invalid for App custom sheets.');
    }
}
